import React, { useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import { StringUtil } from '../../Utils/StringUtil';
import {
    ManagedActionModal,
    ManagedActionModalState,
    ManagedApiError,
    buildManagedActionModalState,
} from '../Shared/ManagedHelpers';
import type { ColumnsType } from 'antd/es/table';
import {
    AddIcon,
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Confirmation,
    DeleteIcon,
    EditIcon,
    FileTextOutlinedIcon,
    Form,
    Input,
    Modal as ServaModal,
    Row,
    Space,
    Spin,
    Table,
    TeamOutlinedIcon,
    Text,
    Title,
    Tooltip,
    Card,
} from '@servahealth/serva-ui-kit';
import Administration from '../../Constants/Endpoints/Administration';
import { SimplifiedTenant } from '../Shared/SimplifiedTenant';
import EnableTenantRequest from '../../Interfaces/Requests/Administration/EnableTenantRequest';
import DisableTenantRequest from '../../Interfaces/Requests/Administration/DisableTenantRequest';
import GetTenantAdminModelsRequest from '../../Interfaces/Requests/Administration/GetTenantAdminModelsRequest';
import GetTenantAdminModelsResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelResponse';
import Tenant from './LNKTenant';
import TenantUsers from './LNKTenantUsers';
import TenantReports from './LNKTenantReports';
import UndeleteIcon from '../Icons/UndeleteIcon';

type AdminTenantProps = {};

const AdminTenant: React.FC<AdminTenantProps> = () => {
    const [tenants, setTenants] = useState<SimplifiedTenant[]>([]);
    const [filteredTenants, setFilteredTenants] = useState<SimplifiedTenant[]>([]);
    const [upsertTenantModalOpen, setUpsertTenantModalOpen] = useState(false);
    const [manageUsersModalOpen, setManageUsersModalOpen] = useState(false);
    const [manageReportsModalOpen, setManageReportsModalOpen] = useState(false);
    const [currentSimplifiedTenant, setCurrentSimplifiedTenant] = useState<SimplifiedTenant | null>();
    const [searchText, setSearchText] = useState('');
    const searchTextRef = useRef('');
    const [isShowDisabled, setIsShowDisabled] = useState(false);
    const [newTenant, setNewTenant] = useState(true);

    const [formAdminTenant] = Form.useForm();

    const [currentError, setCurrentError] = useState('');

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const showAdminTenantInfoModal = (title: string, message: string, isError: boolean = true) => {
        setManageActionModalState(buildManagedActionModalState('info', title, message, isError));
    };

    const handleManageActionModalOk = () => {
        setManageActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const handleAdminTenantFinishFailed = () => {
        showAdminTenantInfoModal(
            'Manage Tenants Error',
            'The Manage Tenants form could not be submitted.  Review the form and try again.'
        );
    };

    const { isLoading: isGetTenantsLoading, isError: isGetTenantsError, error: getTenantsError, refetch: refetchTenants } = useQuery(
        ['admin-tenants', isShowDisabled],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_TENANT_ADMIN_MODELS}`;
                const requestData: GetTenantAdminModelsRequest = {
                    gta_includeDisabled: isShowDisabled,
                };
                return await AxiosUtil.get<GetTenantAdminModelsResponse[], GetTenantAdminModelsRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_TENANT_ADMIN_MODELS: [Get Admin Tenants] Internal Error: ' + err);
                console.error('GET_TENANT_ADMIN_MODELS: [Get Admin Tenants] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('GET_TENANT_ADMIN_MODELS: [Get Admin Tenants] Internal Error: ' + err);
                console.error('GET_TENANT_ADMIN_MODELS: [Get Admin Tenants] Internal Error: ', err);
            },
            onSuccess: (response: GetTenantAdminModelsResponse[]) => {
                if (response) {
                    const currentSearchText = searchTextRef.current;

                    const simplifiedTenants: SimplifiedTenant[] = response.map(
                        (eachElement) => {
                            return {
                                id: eachElement.id,
                                name: eachElement.name,
                                connString: eachElement.connString,
                                connStringRedacted: StringUtil.RedactTenantConnString(eachElement.connString),
                                logoImage: eachElement.logoImage,
                                nameGeneric: eachElement.nameGeneric,
                                hideFromDemo: eachElement.hideFromDemo,
                                sqlFirstDayOfWeek: eachElement.sqlFirstDayOfWeek,
                                applicationId: eachElement.applicationID,
                                linkedDbName: eachElement.linkedDbName,
                                urlBase: eachElement.urlBase,
                                encryptKey: eachElement.encryptKey,
                                encryptCert: eachElement.encryptCert,
                                deleted: eachElement.deleted,
                            };
                        }
                    );

                    setTenants(simplifiedTenants);
                    setFilteredTenants(filterTenantsBySearchText(simplifiedTenants, currentSearchText));
                }
            },
        }
    );

    const { mutate: disTenant, isLoading: isDisTenantLoading, isError: isDisTenantError, error: getDisTenantError } = useMutation(
        async (aTenant: SimplifiedTenant) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.DISABLE_TENANT_REQUEST}`;
                const requestData: DisableTenantRequest = {
                    id: aTenant.id,
                    name: aTenant.name,
                };
                const responseData = await AxiosUtil.post<GetTenantAdminModelsResponse, DisableTenantRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('DISABLE_TENANT_REQUEST: [Disable Tenant] Internal Error: ' + err);
                console.error('DISABLE_TENANT_REQUEST: [Disable Tenant] Internal Error: ', err);
                showAdminTenantInfoModal(
                    'Disable Tenant Error',
                    `Tenant: ${aTenant.name} Was Unable To Be Disabled.  FAILED: Contact Application Support`
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown, aTenant: SimplifiedTenant) => {
                setCurrentError('DISABLE_TENANT_REQUEST: [Disable Tenant] Internal Error: ' + err);
                console.error('DISABLE_TENANT_REQUEST: [Disable Tenant] Internal Error: ', err);
                showAdminTenantInfoModal(
                    'Disable Tenant Error',
                    `Tenant: ${aTenant.name} Was Unable To Be Disabled.  FAILED: Contact Application Support`
                );
            },
            onSuccess: async (response: GetTenantAdminModelsResponse, aTenant: SimplifiedTenant) => {
                await refetchTenants();

                showAdminTenantInfoModal(
                    'Disable Tenant',
                    `Tenant: ${aTenant.name} Was Successfully Disabled.`,
                    false
                );
            },
        }
    );

    const { mutate: enaTenant, isLoading: isEnableTenantLoading, isError: isEnaTenantError, error: getEnaTenantError } = useMutation(
        async (aTenant: SimplifiedTenant) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ENABLE_TENANT_REQUEST}`;
                const requestData: EnableTenantRequest = {
                    id: aTenant.id,
                    name: aTenant.name,
                };
                const responseData = await AxiosUtil.post<GetTenantAdminModelsResponse, EnableTenantRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('ENABLE_TENANT_REQUEST: [Enable Tenant] Internal Error: ' + err);
                console.error('ENABLE_TENANT_REQUEST: [Enable Tenant] Internal Error: ', err);
                showAdminTenantInfoModal(
                    'Restore Tenant Error',
                    `Tenant: ${aTenant.name} Was Unable To Be Restored ([Re] Enabled.)  FAILED: Contact Application Support`
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown, aTenant: SimplifiedTenant) => {
                setCurrentError('ENABLE_TENANT_REQUEST: [Enable Tenant] Internal Error: ' + err);
                console.error('ENABLE_TENANT_REQUEST: [Enable Tenant] Internal Error: ', err);
                showAdminTenantInfoModal(
                    'Restore Tenant Error',
                    `Tenant: ${aTenant.name} Was Unable To Be Restored ([Re] Enabled.)  FAILED: Contact Application Support`
                );
            },
            onSuccess: async (response: GetTenantAdminModelsResponse, aTenant: SimplifiedTenant) => {
                await refetchTenants();

                showAdminTenantInfoModal(
                    'Restore Tenant',
                    `Tenant: ${aTenant.name} Was Successfully Restored ([Re] Enabled.)`,
                    false
                );
            },
        }
    );

    const columns: ColumnsType<SimplifiedTenant> = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 60, ellipsis: true,
            onCell: (record: SimplifiedTenant) => ({
                style: { color: !record.deleted ? 'black' : 'red' },
            }),
            render: (text: number) => <div>{text}</div>,
        },
        { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
        { title: 'Conn. String', dataIndex: 'connStringRedacted', key: 'connStringRedacted', ellipsis: true },
        { title: 'Generic Name', dataIndex: 'nameGeneric', key: 'nameGeneric', ellipsis: true },
        { title: 'Demo Hide ?', dataIndex: 'hideFromDemo', key: 'hideFromDemo', width: 115, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'SQL 1st DOW', dataIndex: 'sqlFirstDayOfWeek', key: 'sqlFirstDayOfWeek', align: 'center',
            render: (text: number) => {
                const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',];
                return <div>{dayNames[text - 1] + ' [' + String(text) + ']'}</div>
            },
        },
        { title: 'Linked DB Name', dataIndex: 'linkedDbName', key: 'linkedDbName',  ellipsis: true },
        { title: 'URL Base', dataIndex: 'urlBase', key: 'urlBase', ellipsis: true },
        { title: 'Encrypt Key', dataIndex: 'encryptKey', key: 'encryptKey', ellipsis: true },
        { title: 'Encrypt Cert', dataIndex: 'encryptCert', key: 'encryptCert', ellipsis: true },
        { title: 'Action', key: 'action', width: 130, fixed: 'right',
            render: (text: unknown, record: SimplifiedTenant) => (
                <Space size={0}>
                    <Tooltip title='Edit Tenant'>
                        <Button
                            size='small'
                            className='m-0 p-0'
                            type='link'
                            icon={<EditIcon />}
                            onClick={() => handleUpdateTenant(record)}
                        />
                    </Tooltip>
                    <Tooltip title='Manage Reports'>
                        <Button
                            size='small'
                            className='m-0 p-0'
                            type='link'
                            icon={<FileTextOutlinedIcon />}
                            onClick={() => handleManageReports(record)}
                        />
                    </Tooltip>
                    <Tooltip title='Manage Users'>
                        <Button
                            size='small'
                            className='m-0 p-0'
                            type='link'
                            icon={<TeamOutlinedIcon />}
                            onClick={() => handleManageUsers(record)}
                        />
                    </Tooltip>
                    {
                        (!record.deleted ?
                            <Tooltip title='Disable Tenant'>
                                <Confirmation
                                    title='Are you sure you want to disable this tenant?'
                                    onConfirm={() => disTenant(record)}
                                    okText='Yes'
                                    cancelText='No'
                                >
                                    <Button 
                                        size='small'
                                        className='m-0 p-0'
                                        danger 
                                        type='link' 
                                        icon={<DeleteIcon />} 
                                    />
                                </Confirmation>
                            </Tooltip>
                            :
                            <Tooltip title='Restore ( [Re] Enable ) Tenant'>
                                <Confirmation
                                    title='Are you sure you want to restore ( [re] enable ) this tenant?'
                                    onConfirm={() => enaTenant(record)}
                                    okText='Yes'
                                    cancelText='No'
                                >
                                    <Button
                                        size='small'
                                        className='m-0 p-0'
                                        type='link'
                                        icon={<UndeleteIcon />}
                                    />
                                </Confirmation>
                            </Tooltip>
                        )
                    }
                </Space>
            ),
        },
    ];

    const handleSearchTextChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        const value = aValue.target.value;

        searchTextRef.current = value;
        setSearchText(value);
        setFilteredTenants(filterTenantsBySearchText(tenants, value));
    };

    const handleIsShowDisabledChange = (aValue: { target: { checked: boolean } }) => {
        setIsShowDisabled(aValue.target.checked);
    };

    const handleUpdateTenant = (aTenant: SimplifiedTenant) => {
        setNewTenant(false);
        setCurrentSimplifiedTenant(aTenant);
        setUpsertTenantModalOpen(true);
    };

    const handleManageReports = (aTenant: SimplifiedTenant) => {
        setManageReportsModalOpen(true);
        setCurrentSimplifiedTenant(aTenant);
    };

    const handleManageUsers = (aTenant: SimplifiedTenant) => {
        setManageUsersModalOpen(true);
        setCurrentSimplifiedTenant(aTenant);
    };

    const handleCreateTenant = () => {
        setNewTenant(true);
        setCurrentSimplifiedTenant(null);
        setUpsertTenantModalOpen(true);
    };

    const filterTenantsBySearchText = (tenantRows: SimplifiedTenant[], value: string): SimplifiedTenant[] => {
        const lowerCaseSearchText = value.trim().toLocaleLowerCase();

        if (lowerCaseSearchText === '') {
            return tenantRows;
        }

        return tenantRows.filter((eachTenant) => {
            return eachTenant.name.toLowerCase().includes(lowerCaseSearchText);
        });
    };

    const doSearch = (sValue: string) => {
        searchTextRef.current = sValue;
        setSearchText(sValue);
        setFilteredTenants(filterTenantsBySearchText(tenants, sValue));
    };

    const determineWhichError = () => {
        if ( isGetTenantsError ) {
            return currentError + ' ' + ((getTenantsError as ManagedApiError).response?.data?.message ?? '');
        }
        else if ( isDisTenantError ) {
            return currentError + ' ' + ((getDisTenantError as ManagedApiError).response?.data?.message ?? '');
        }
        else if ( isEnaTenantError ) {
            return currentError + ' ' + ((getEnaTenantError as ManagedApiError).response?.data?.message ?? '');
        }
        else {
            return '';
        }
    };

    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageActionModalOk}
                onCancel={handleManageActionModalOk}
            />
            <ServaModal
                hideButtons={true}
                open={upsertTenantModalOpen}
                onCancel={() => setUpsertTenantModalOpen(false)}
            >
                <Tenant
                    id={currentSimplifiedTenant?.id ?? 0}
                    name={currentSimplifiedTenant?.name ?? ''}
                    connString={currentSimplifiedTenant?.connString ?? ''}
                    logoImage={currentSimplifiedTenant?.logoImage ?? ''}
                    nameGeneric={currentSimplifiedTenant?.nameGeneric ?? ''}
                    hideFromDemo={currentSimplifiedTenant?.hideFromDemo ?? false}
                    sqlFirstDayOfWeek={currentSimplifiedTenant?.sqlFirstDayOfWeek ?? 7}
                    linkedDbName={currentSimplifiedTenant?.linkedDbName ?? ''}
                    urlBase={currentSimplifiedTenant?.urlBase ?? ''}
                    encryptKey={currentSimplifiedTenant?.encryptKey ?? ''}
                    encryptCert={currentSimplifiedTenant?.encryptCert ?? ''}
                    newTenant={newTenant}
                    close={() => setUpsertTenantModalOpen(false)}
                    refetch={() => refetchTenants()}
                ></Tenant>
            </ServaModal>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={manageReportsModalOpen}
                width='65%'
                onCancel={() => setManageReportsModalOpen(false)}
            >
                <TenantReports
                    tenantId={currentSimplifiedTenant?.id ?? 0}
                    tenantName={currentSimplifiedTenant?.name ?? ''}
                    close={() => setManageReportsModalOpen(false)}
                ></TenantReports>
            </ServaModal>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={manageUsersModalOpen}
                width='65%'
                onCancel={() => setManageUsersModalOpen(false)}
            >
                <TenantUsers
                    tenantId={currentSimplifiedTenant?.id ?? 0}
                    tenantName={currentSimplifiedTenant?.name ?? ''}
                    close={() => setManageUsersModalOpen(false)}
                ></TenantUsers>
            </ServaModal>
            <Spin spinning={
                            isGetTenantsLoading ||
                            isDisTenantLoading ||
                            isEnableTenantLoading
                           }>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetTenantsError ||
                        isDisTenantError ||
                        isEnaTenantError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Tenants And Tenant Links To Reports And Users</Title>
                    </Text>
                </Space>
                <Form
                    form={formAdminTenant}
                    initialValues={{
                        AtIsShowDisabled: false,
                    }}
                    key='admintenant'
                    onFinishFailed={handleAdminTenantFinishFailed}
                >
                    <Row gutter={16} style={{ padding: '16px' }}>
                        <Col span={2}>
                            <Button
                                type='primary'
                                style={{
                                    marginBottom: '15px',
                                    float: 'left',
                                }}
                                icon={<AddIcon />}
                                onClick={handleCreateTenant}
                            >
                                Create Tenant
                            </Button>
                        </Col>
                        <Col span={2}></Col>
                        <Col span={8}>
                            <Input
                                allowClear={true}
                                search={true}
                                style={{width: '75%'}}
                                value={searchText}
                                onChange={handleSearchTextChange}
                                onSearch={doSearch}
                            ></Input>
                        </Col>
                        <Col span={3}></Col>
                        <Col span={3}>
                            <div
                                style={{
                                    height: '30px',
                                    paddingLeft: '5px'
                                }}
                            >
                                <Form.Item
                                    key='atisshowdisabled'
                                    name='AtIsShowDisabled'
                                    valuePropName='checked'
                                    style={{verticalAlign: 'middle'}}
                                >
                                    <Checkbox
                                        checked={isShowDisabled}
                                        style={{float: 'left', verticalAlign: 'middle'}}
                                        onChange={handleIsShowDisabledChange}
                                    >
                                        Show Disabled
                                    </Checkbox>
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                    <Table
                        bordered
                        className='table-alternating-row-color'
                        columns={columns}
                        dataSource={filteredTenants}
                        rowClassName={() => 'rowClassName1'}
                        rowKey='id'
                        size='small'
                        style={{ padding: '12px' }}
                    />
                </Form>
            </Spin>
        </Card>
    );
};

export default AdminTenant;
